import { gql, useApolloClient } from '@apollo/client';
import { navigate } from '@reach/router';
import {
  GameDifficulty,
  GamePreviewType,
  GetGamePreview,
  GetTutorialQuery,
  GetTutorialQuery_me_tutorial,
} from 'graphql/types';
import { useState } from 'react';
import { useMutationErrorHandler } from 'utils/mutation-utils';
import { useCreateTutorial } from './useCreateTutorial';

export function useStartTutorial() {
  const client = useApolloClient();
  const handleError = useMutationErrorHandler();
  const [createTutorial, { loading: creatingTutorial }] = useCreateTutorial();
  const [startingTutorial, setStartingTutorial] = useState(false);
  const [unfinishedTutorial, setUnfinishedTutorial] = useState<
    GetTutorialQuery_me_tutorial | undefined
  >();

  const handleStartTutorial = async () => {
    try {
      setStartingTutorial(true);
      const { data: gamePreviewData } = await client.query<GetGamePreview>({
        query: getGamePreviewQuery,
        variables: {
          input: { gamePreviewType: GamePreviewType.MEDIUM_GAME_INTRO },
        },
        fetchPolicy: 'cache-only',
      });
      if (gamePreviewData.me?.gameDifficulty === GameDifficulty.MEDIUM) {
        navigate(`/game/${gamePreviewData.gamePreviewId}`);
        return;
      }
      // We want to query on button click and not when mounted because we dont wanna do decisions based on stale data
      const { data } = await client.query<GetTutorialQuery>({
        query: getTutorialQuery,
        fetchPolicy: 'network-only',
      });
      const tutorial = data?.me?.tutorial;
      if (!tutorial) {
        // This should not happen but if it does, we use network-only policy so the "ProtectedRoute" will be notified that we are not authenticated anymore
        // and will handle that
        return;
      }

      if (tutorial.game == null) {
        await startNewTutorial();
      } else {
        setUnfinishedTutorial(tutorial);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setStartingTutorial(false);
    }
  };

  const closeUnfinishedTutorialDialog = () => {
    setUnfinishedTutorial(undefined);
  };

  const continueTutorial = () => {
    closeUnfinishedTutorialDialog();
    navigate(`/game/${unfinishedTutorial!.game!.id}`);
  };

  const startNewTutorial = async () => {
    closeUnfinishedTutorialDialog();
    const gameId = await createTutorial();
    if (!gameId) return;
    navigate(`/game/${gameId}`);
  };

  const restartTutorial = () => {
    closeUnfinishedTutorialDialog();
    startNewTutorial();
  };

  return {
    unfinishedTutorial,
    startingTutorial,
    creatingTutorial,
    continueTutorial,
    restartTutorial,
    handleStartTutorial,
    closeUnfinishedTutorialDialog,
  } as const;
}

const getTutorialQuery = gql`
  query GetTutorialQuery {
    me {
      id
      tutorial {
        finished
        game {
          id
          status
        }
      }
    }
  }
`;

const getGamePreviewQuery = gql`
  query GetGamePreview($input: GamePreviewIdInput!) {
    me {
      id
      gameDifficulty
    }
    gamePreviewId(input: $input)
  }
`;
